import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DocumentCategory {
  category_id: string;
  clinic_id:   string | null;
  name:        string;
  code:        string;
  is_default:  boolean;
  is_active:   boolean;
  sort_order:  number;
}

export interface UploadUrlItem {
  s3_key:     string;
  upload_url: string | null;
}

export interface UploadUrlsResponse {
  upload_batch_id: string;
  files:           UploadUrlItem[];
}

export interface ConfirmDocumentItem {
  s3_key:               string;
  file_name:            string;
  mime_type:            string;
  file_size_bytes:      number;
  upload_batch_id:      string;
  category_id?:         string | null;
  visit_id?:            string | null;
  test_date?:           string | null;
  source_name?:         string | null;
  ordering_doctor_name?: string | null;
  notes?:               string | null;
  tags?:                string[];
  is_sensitive?:        boolean;
}

export interface PatientDocument {
  document_id:          string;
  patient_id:           string;
  visit_id:             string | null;
  visit_date:           string | null;
  category_id:          string | null;
  s3_key:               string;
  preview_s3_key:       string | null;
  file_name:            string;
  mime_type:            string;
  file_size_bytes:      number;
  page_count:           number | null;
  test_date:            string | null;
  source_name:          string | null;
  ordering_doctor_name: string | null;
  ordering_doctor_id:   string | null;
  notes:                string | null;
  tags:                 string[];
  is_sensitive:         boolean;
  upload_batch_id:      string | null;
  uploaded_by:          string | null;
  uploaded_at:          string;
  updated_at:           string;
}

export interface DocumentListFilters {
  category_id?: string;
  tag?:         string;
  date_from?:   string;
  date_to?:     string;
  q?:           string;
  visit_id?:    string;
}

export interface DocumentUrlResponse {
  url:        string;
  expires_in: number;
}

export interface ZipResponse {
  url:         string;
  expires_in:  number;
  file_count:  number;
  total_bytes: number;
}

// ─── S3 direct upload ─────────────────────────────────────────────────────────

/** PUT a file directly to S3 using the presigned URL. No auth headers — S3 handles it. */
export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const DocumentAPI = {
  listCategories(patientId: string): Promise<DocumentCategory[]> {
    return apiRequest<DocumentCategory[]>(ROUTES.PATIENT_DOCUMENT_CATEGORIES(patientId));
  },

  requestUploadUrls(
    patientId: string,
    files: Array<{ file_name: string; mime_type: string; file_size_bytes: number }>,
  ): Promise<UploadUrlsResponse> {
    return apiRequest<UploadUrlsResponse>(ROUTES.PATIENT_DOCUMENT_UPLOAD_URLS(patientId), {
      method: "POST",
      body: JSON.stringify({ files }),
    });
  },

  confirm(patientId: string, documents: ConfirmDocumentItem[]): Promise<PatientDocument[]> {
    return apiRequest<PatientDocument[]>(ROUTES.PATIENT_DOCUMENT_CONFIRM(patientId), {
      method: "POST",
      body: JSON.stringify({ documents }),
    });
  },

  list(patientId: string, filters?: DocumentListFilters): Promise<PatientDocument[]> {
    const params = new URLSearchParams();
    if (filters?.category_id) params.set("category_id", filters.category_id);
    if (filters?.tag)         params.set("tag", filters.tag);
    if (filters?.date_from)   params.set("date_from", filters.date_from);
    if (filters?.date_to)     params.set("date_to", filters.date_to);
    if (filters?.q)           params.set("q", filters.q);
    if (filters?.visit_id)    params.set("visit_id", filters.visit_id);
    const qs = params.toString();
    return apiRequest<PatientDocument[]>(`${ROUTES.PATIENT_DOCUMENTS(patientId)}${qs ? `?${qs}` : ""}`);
  },

  getUrl(patientId: string, documentId: string): Promise<DocumentUrlResponse> {
    return apiRequest<DocumentUrlResponse>(ROUTES.PATIENT_DOCUMENT_URL(patientId, documentId));
  },

  getPreviewUrl(patientId: string, documentId: string): Promise<DocumentUrlResponse> {
    return apiRequest<DocumentUrlResponse>(ROUTES.PATIENT_DOCUMENT_PREVIEW_URL(patientId, documentId));
  },

  patchSensitivity(patientId: string, documentId: string, isSensitive: boolean): Promise<PatientDocument> {
    return apiRequest<PatientDocument>(ROUTES.PATIENT_DOCUMENT_SENSITIVITY(patientId, documentId), {
      method: "PATCH",
      body: JSON.stringify({ is_sensitive: isSensitive }),
    });
  },

  delete(patientId: string, documentId: string): Promise<void> {
    return apiRequest<void>(ROUTES.PATIENT_DOCUMENT(patientId, documentId), { method: "DELETE" });
  },

  zip(patientId: string, documentIds: string[]): Promise<ZipResponse> {
    return apiRequest<ZipResponse>(ROUTES.PATIENT_DOCUMENT_ZIP(patientId), {
      method: "POST",
      body: JSON.stringify({ document_ids: documentIds }),
    });
  },
};
